<template>
  <div class="goods-list">
    <van-nav-bar :title="shopName" left-arrow @click-left="$router.back()" />

    <!-- 搜索框 -->
    <div class="search-container">
      <van-search v-model="searchKeyword" placeholder="搜索商品" shape="round" @input="onSearchInput" @clear="onSearchClear"
        clearable />
    </div>

    <div class="goods-container">
      <div v-for="item in goodsList" :key="item.goodsId" class="goods-item" @click="handleItemClick(item)">
        <div class="goods-image">
          <img :src="item.goodsImg" :alt="item.goodsName" />
          <div class="sold-out-mask" v-if="item.isSoldOut">
            <span>已售罄</span>
          </div>
        </div>
        <div class="goods-content">
          <div class="goods-info">
            <h3 class="goods-name">{{ item.goodsName }}</h3>
            <div class="goods-tags">
              <span v-for="tag in item.skuTagDataList" :key="tag" class="tag">{{
                tag
              }}</span>
            </div>
            <div class="goods-desc" v-html="item.customTag"></div>
          </div>
          <div class="goods-action">
            <van-button type="primary" size="small" round class="add-btn" :disabled="item.isSoldOut">
              <van-icon name="plus" />
            </van-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 商品详情弹出层 -->
    <van-popup v-model="showDetail" position="bottom" round closeable close-icon="close" :style="{ height: '85%' }">
      <div class="detail-popup" v-if="goodsDetail">
        <div class="scrollable-content">
          <!-- 商品主图 -->
          <div class="goods-image">
            <img :src="goodsDetail.goodsImg" :alt="goodsDetail.goodsName" />
          </div>

          <!-- 商品信息 -->
          <div class="goods-info">
            <h1 class="goods-name">{{ goodsDetail.goodsName }}</h1>
            <div class="goods-tags">
              <span v-for="tag in goodsDetail.skuTagDataList" :key="tag" class="tag">{{ tag }}</span>
            </div>
            <div class="goods-desc" v-html="goodsDetail.customTag"></div>
            <div v-if="goodsDetail.unitList && goodsDetail.unitList.length" class="unit-list">
              <div v-for="unit in goodsDetail.unitList" :key="unit.unitId" class="unit-item"
                :class="{ active: unit.isSelect }" @click="selectUnit(unit)">
                {{ unit.unitName }}
              </div>
            </div>
          </div>

          <!-- 商品选项 -->
          <template v-if="goodsDetail && goodsDetail.modifyList">
            <div v-for="group in goodsDetail.modifyList" :key="group.typeName" class="specs-section">
              <div class="section-title">{{ group.typeName }}</div>
              <div class="options-list">
                <div v-for="option in group.data" :key="option.goodsId" class="option-item" :class="{
                  active:
                    selectedOptions[group.typeName] ===
                    option.goodsName,
                }" @click="
                  selectOption(group.typeName, option.goodsName)
                  ">
                  {{ option.goodsName }}
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- 底部购买栏 -->
        <div class="bottom-bar">
          <van-button type="primary" block @click="goToSettlement" :disabled="!isValidSelection">
            结算
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import request from "@/utils/request";
import { mapState } from "vuex";

export default {
  name: "GoodsList",
  data() {
    return {
      shopName: "",
      shopAddress: "", // 1. 添加 shopAddress
      goodsList: [],
      allGoodsList: [], // 存储所有商品数据
      searchKeyword: "", // 搜索关键词
      searchTimer: null, // 搜索防抖定时器
      showDetail: false,
      currentGoods: null,
      goodsDetail: null,
      selectedOptions: {},
    };
  },
  computed: {
    ...mapState(["appConfig", "memberCoupon"]),

    isValidSelection() {
      if (!this.goodsDetail) return false;

      // 1. 检查是否已选择杯型
      const isUnitSelected =
        this.goodsDetail.unitList &&
        this.goodsDetail.unitList.some((unit) => unit.isSelect);

      // 2. 检查是否所有必选的口味选项都已选择
      const areOptionsSelected = (
        this.goodsDetail.sortedPracticeList || []
      ).every((group) => {
        if (group.isRequired) {
          // isRequired > 0 表示必选
          return !!this.selectedOptions[group.practiceName];
        }
        return true;
      });

      // 3. 两者都满足时，才算有效选择
      return isUnitSelected && areOptionsSelected;
    },
  },
  created() {
    this.shopName = this.$route.query.shopName || "";
    this.shopAddress = this.$route.query.shopAddress || ""; // 2. 从路由获取 shopAddress
    
    // 打印 Vuex 中的 memberCoupon 对象
    console.log("=== Vuex memberCoupon 完整对象 ===");
    console.log(JSON.stringify(this.memberCoupon, null, 2));
    console.log("=== memberCoupon 字段检查 ===");
    console.log("promotionId:", this.memberCoupon?.promotionId);
    console.log("couponId:", this.memberCoupon?.couponId);
    console.log("couponName:", this.memberCoupon?.couponName);
    console.log("promotionName:", this.memberCoupon?.promotionName);
    console.log("templateId:", this.memberCoupon?.templateId);
    console.log("promotionTemplateId:", this.memberCoupon?.promotionTemplateId);
    console.log("level:", this.memberCoupon?.level);
    
    this.fetchGoodsList();
  },
  methods: {
    async fetchGoodsList() {
      const loading = this.$toast.loading({
        message: "加载中...",
        forbidClick: true,
        duration: 0,
      });
      try {
        const shopId = this.$route.query.shopId;
        const customerShopCode = this.$route.query.customerShopCode;

        if (!shopId) {
          this.$toast.fail("缺少店铺ID");
          loading.clear();
          return;
        }

        // 从 Vuex 获取优惠券模板 ID
        const couponId = this.memberCoupon ? this.memberCoupon.promotionId : undefined;

        // 调用查找优惠券可用菜单接口
        const params = {
          channelCode: 20,
          brandId: 1,
          shopId: shopId,
          couponId: couponId,
          customerShopCode: customerShopCode,
        };

        console.log("查询优惠券可用商品参数：", params);
        let listResponse = await request.post("/gm/find-coupon-menu", params);

        if (listResponse) {
          listResponse = listResponse.data;
        }

        console.log("商品列表返回数据：", listResponse);

        if (
          !listResponse ||
          !listResponse.menu ||
          listResponse.menu.code !== 0 ||
          !listResponse.menu.data
        ) {
          this.$toast.fail(listResponse?.menu?.msg || "获取商品列表失败");
          this.goodsList = [];
          loading.clear();
          return;
        }

        const menuData = listResponse.menu.data; // 分类数组
        
        // 从所有分类中提取商品列表并合并，使用 Map 去重
        const goodsMap = new Map();
        if (Array.isArray(menuData)) {
          menuData.forEach(category => {
            if (category.cmenuShopProductAOList && Array.isArray(category.cmenuShopProductAOList)) {
              category.cmenuShopProductAOList.forEach(product => {
                // 使用 productId 作为唯一标识，避免重复
                if (!goodsMap.has(product.productId)) {
                  goodsMap.set(product.productId, {
                    ...product,
                    categoryId: category.categoryId, // 保存分类ID用于详情接口
                  });
                }
              });
            }
          });
        }

        const baseGoodsList = Array.from(goodsMap.values());

        if (baseGoodsList.length === 0) {
          this.goodsList = [];
          this.$toast("暂无可用商品");
          loading.clear();
          return;
        }

        console.log("基础商品列表（已去重）：", baseGoodsList);

        // 直接处理古茗接口返回的商品数据
        const processedGoodsList = baseGoodsList.map((item) => {
          // 解析标签
          let tagAOList = [];
          try {
            if (item.tag) {
              tagAOList = JSON.parse(item.tag);
            } else if (item.tagAOList) {
              tagAOList = item.tagAOList;
            }
          } catch (e) {
            console.error("解析标签失败：", e);
            tagAOList = item.tagAOList || [];
          }

          // 提取 SKU 列表作为杯型，过滤掉大杯
          const displayUnitList = (item.cmenuShopProductSkuAOS || [])
            .filter((sku) => sku.skuName !== "大杯" && sku.skuName !== "大") // 过滤掉大杯
            .map((sku) => ({
              unitId: sku.productSkuId,
              unitName: sku.skuName,
              isSelect: false,
              salePrice: sku.realSellPrice,
              skuCode: sku.skuCode,
            }));

          return {
            goodsId: item.productId,
            menuProductId: item.menuProductId,
            goodsName: item.productName,
            goodsImg: item.mainPicture || item.simplePicture,
            isSoldOut: item.sellState !== 0 || !item.sellEligibility,
            skuTagDataList: tagAOList.map((tag) => tag.value),
            customTag: item.sketch,
            minPrice: item.minRealPrice,
            displayUnitList,
            cmenuShopProductSkuAOS: item.cmenuShopProductSkuAOS,
            // 保留原始数据
            ...item,
          };
        })

        // 保存所有商品数据用于搜索
        this.allGoodsList = processedGoodsList;
        this.goodsList = processedGoodsList;
      } catch (error) {
        console.error("获取商品列表失败:", error);
        this.$toast.fail("获取商品列表失败");
      } finally {
        loading.clear();
      }
    },
    handleItemClick(item) {
      if (item.isSoldOut) {
        this.$toast("该商品已售罄");
        return;
      }
      if (item._loadError) {
        this.$toast.fail("商品信息加载失败，无法查看详情");
        return;
      }
      this.showGoodsDetail(item);
    },
    // 添加一个判断接口成功的方法
    isSuccess(code) {
      return code === 0 || code === "0" || code === "00" || code === "000";
    },
    async showGoodsDetail(item) {
      this.currentGoods = item;
      this.selectedOptions = {}; // 重置选项

      const loading = this.$toast.loading({
        message: "加载中...",
        forbidClick: true,
        duration: 0,
      });

      try {
        // 调用商品详情接口
        const params = {
          channelCode: 20,
          brandId: 1,
          shopId: this.$route.query.shopId,
          customerShopCode: this.$route.query.customerShopCode,
          position: 1,
          categoryId: item.categoryId,
          moduleKey: "COUPON-AVAILABLE-PRODUCT",
          productId: item.productId,
        };

        console.log("获取商品详情参数：", params);
        let detailResponse = await request.post("/gm/get-product-detail", params);

        if (detailResponse) {
          detailResponse = detailResponse.data;
        }

        console.log("商品详情返回数据：", detailResponse);

        if (
          !detailResponse ||
          !detailResponse.product ||
          detailResponse.product.code !== 0 ||
          !detailResponse.product.data
        ) {
          this.$toast.fail(detailResponse?.product?.msg || "获取商品详情失败");
          loading.clear();
          return;
        }

        const detailData = detailResponse.product.data;

        // 1. 处理 SKU 列表（杯型/规格），过滤掉大杯
        let finalUnitList = (detailData.cmenuShopProductSkuAOList || [])
          .filter((sku) => sku.skuName !== "大杯" && sku.skuName !== "大") // 过滤掉大杯
          .map((sku, index) => ({
            unitId: sku.productSkuId,
            unitName: sku.skuName,
            isSelect: index === 0,
            salePrice: sku.realSellPrice,
            skuCode: sku.skuCode,
            ...sku,
          }));

        // 2. 处理属性列表（温度、甜度等），只显示必选项且不加钱的选项
        const modifyList = (detailData.cmenuShopProductAttrAOList || [])
          .filter((attr) => attr.isMust === 1) // 只保留必选项
          .map((attr) => ({
            typeName: attr.attrName,
            attrId: attr.attrId,
            groupMinChoose: attr.isMust,
            type: attr.type,
            data: (attr.cmenuShopProductAttrValueAOList || [])
              .filter((opt) => !opt.price || opt.price === 0) // 过滤掉需要加钱的选项
              .map((opt) => ({
                goodsId: opt.attrValueId,
                goodsName: opt.attrValue,
                isSelect: opt.isDefault === 1,
                price: opt.price || 0,
                ...opt,
              })),
          }))
          .filter((attr) => attr.data.length > 0); // 过滤掉没有选项的属性组

        // 3. 组合成UI需要的数据结构
        this.goodsDetail = {
          ...item,
          ...detailData,
          goodsId: detailData.productId,
          goodsName: detailData.productName,
          goodsImg: detailData.mainPicture || item.mainPicture,
          unitList: finalUnitList,
          modifyList,
          cmenuShopProductAttrAOList: detailData.cmenuShopProductAttrAOList,
          cmenuShopProductSkuAOList: detailData.cmenuShopProductSkuAOList,
        };

        // 4. 设置默认选中的选项
        if (this.goodsDetail.modifyList) {
          this.goodsDetail.modifyList.forEach((group) => {
            const defaultOption =
              group.data.find((opt) => opt.isSelect) || group.data[0];
            if (defaultOption) {
              this.$set(
                this.selectedOptions,
                group.typeName,
                defaultOption.goodsName
              );
            }
          });
        }

        this.showDetail = true;
        loading.clear();
      } catch (error) {
        console.error("处理商品详情出错：", error);
        this.$toast.fail("打开商品详情失败");
        loading.clear();
      }
    },
    selectOption(groupName, optionName) {
      this.$set(this.selectedOptions, groupName, optionName);
    },
    async goToSettlement() {
      if (!this.isValidSelection) {
        this.$toast("请选择必选项");
        return;
      }

      const selectedUnit = this.goodsDetail.unitList.find((u) => u.isSelect);
      
      console.log("goodsDetail", this.goodsDetail);
      console.log("selectedUnit", selectedUnit);

      if (!selectedUnit) {
        this.$toast.fail("请选择规格");
        return;
      }

      const loading = this.$toast.loading({
        message: "加载中...",
        forbidClick: true,
        duration: 0,
      });

      try {
        // 构建属性列表
        const attrList = [];
        (this.goodsDetail.cmenuShopProductAttrAOList || []).forEach((attr) => {
          if (attr.isMust === 1) { // 只处理必选项
            const selectedOptionName = this.selectedOptions[attr.attrName];
            if (selectedOptionName) {
              const selectedOption = (attr.cmenuShopProductAttrValueAOList || []).find(
                (opt) => opt.attrValue === selectedOptionName
              );
              if (selectedOption && (!selectedOption.price || selectedOption.price === 0)) {
                attrList.push({
                  attrId: attr.menuProductAttrId,
                  attrName: attr.attrName,
                  type: attr.type,
                  attrValueList: [
                    {
                      attrValueName: selectedOption.attrValue,
                      attrValueId: selectedOption.menuProductAttrValueId,
                      price: selectedOption.price || 0,
                      originAttrValueId: selectedOption.attrValueId,
                    },
                  ],
                  originAttrId: attr.attrId,
                });
              }
            }
          }
        });

        // 构建购物车参数
        const params = {
          channelCode: 20,
          brandId: 1,
          shopId: this.$route.query.shopId,
          customerShopCode: this.$route.query.customerShopCode,
          cartType: "SELF_PICK",
          skuInfo: {
            productInfo: {
              menuProductId: this.goodsDetail.menuProductId,
              menuId: this.goodsDetail.menuId || "0",
              productId: this.goodsDetail.productId,
              productType: this.goodsDetail.type || 0,
              productName: this.goodsDetail.productName,
            },
            productSkuId: selectedUnit.productSkuId,
            skuName: selectedUnit.skuName,
            quantity: 1,
          },
          attrList: attrList,
          couponInfo: this.memberCoupon ? {
            couponId: this.memberCoupon.couponId,
            couponName: this.memberCoupon.couponName,
            promotionTemplateId: this.memberCoupon.templateId,
            level: this.memberCoupon.level,
          } : {},
        };

        console.log("memberCoupon 数据：", this.memberCoupon);
        console.log("加入购物车参数：", params);
        
        // 先清空购物车
        const clearCartParams = {
          channelCode: 20,
          brandId: 1,
          shopId: this.$route.query.shopId,
          customerShopCode: this.$route.query.customerShopCode,
        };
        console.log("清空购物车参数：", clearCartParams);
        await request.post("/gm/clear-cart", clearCartParams);
        console.log("购物车已清空");
        
        let response = await request.post("/gm/add-to-cart", params);

        if (response) {
          response = response.data;
        }

        console.log("加入购物车返回：", response);

        if (response && response.cart && response.cart.code === 0) {
          this.$toast.success("已加入购物车");
          loading.clear();
          
          // 跳转到订单确认页面
          this.$router.push({
            path: "/goods-detail",
            query: {
              shopId: this.$route.query.shopId,
              customerShopCode: this.$route.query.customerShopCode,
              shopName: this.shopName,
              shopAddress: this.shopAddress,
            },
          });
        } else {
          this.$toast.fail(response?.cart?.msg || "加入购物车失败");
          loading.clear();
        }
      } catch (error) {
        console.error("加入购物车失败：", error);
        this.$toast.fail("加入购物车失败");
        loading.clear();
      }
    },
    // 生成UUID
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    // 格式化日期为 yyyyMMddHHmmss
    formatDate(date) {
      const pad = (num) => (num < 10 ? "0" + num : num);
      return (
        `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
          date.getDate()
        )}` +
        `${pad(date.getHours())}${pad(date.getMinutes())}${pad(
          date.getSeconds()
        )}`
      );
    },
    // 新增：选择规格容量
    selectUnit(unit) {
      // 必须通过创建新数组的方式来触发响应式更新
      this.goodsDetail.unitList = this.goodsDetail.unitList.map((u) => ({
        ...u,
        isSelect: u.unitId === unit.unitId,
      }));
    },
    // 搜索输入处理（防抖）
    onSearchInput() {
      // 清除之前的定时器
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      // 设置新的定时器，300ms后执行搜索
      this.searchTimer = setTimeout(() => {
        this.performSearch();
      }, 300);
    },
    // 搜索清除处理
    onSearchClear() {
      this.searchKeyword = "";
      this.goodsList = this.allGoodsList;
    },
    // 执行搜索
    performSearch() {
      if (!this.searchKeyword.trim()) {
        // 如果搜索关键词为空，显示所有商品
        this.goodsList = this.allGoodsList;
        return;
      }

      const keyword = this.searchKeyword.toLowerCase().trim();

      // 根据商品名称、标签和描述进行搜索
      this.goodsList = this.allGoodsList.filter((item) => {
        // 搜索商品名称
        const nameMatch =
          item.goodsName && item.goodsName.toLowerCase().includes(keyword);

        // 搜索商品标签
        const tagMatch =
          item.skuTagDataList &&
          item.skuTagDataList.some((tag) =>
            tag.toLowerCase().includes(keyword)
          );

        // 搜索商品描述（去除HTML标签后搜索）
        const descMatch =
          item.customTag &&
          item.customTag
            .replace(/<[^>]*>/g, "")
            .toLowerCase()
            .includes(keyword);

        return nameMatch || tagMatch || descMatch;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.goods-list {
  min-height: 100vh;
  background: #f7f8fa;
}

.search-container {
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;

  :deep(.van-search) {
    padding: 0;
    background: transparent;
  }

  :deep(.van-search__content) {
    background: #f7f8fa;
    border-radius: 18px;
  }

  :deep(.van-field__control) {
    font-size: 14px;
  }
}

.goods-container {
  padding: 12px;
}

.goods-item {
  display: flex;
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}

.goods-image {
  width: 120px;
  height: 120px;
  margin-right: 12px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .sold-out-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      color: #fff;
      font-size: 16px;
      padding: 4px 12px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 4px;
    }
  }
}

.goods-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.goods-info {
  .goods-name {
    font-size: 16px;
    font-weight: 500;
    margin: 0 0 8px;
    line-height: 1.4;
  }

  .goods-tags {
    margin-bottom: 8px;

    .tag {
      display: inline-block;
      padding: 2px 6px;
      margin-right: 6px;
      background: #f5f5f5;
      color: #666;
      border-radius: 4px;
      font-size: 12px;
    }
  }

  .goods-desc {
    >span {
      font-size: 12px;
      color: #999;
    }

    font-size: 12px;
    color: #999;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }
}

.goods-action {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: 8px;

  .add-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &[disabled] {
      opacity: 0.5;
      background: #ccc;
      border-color: #ccc;
    }
  }
}

.detail-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  .goods-image {
    width: 100%;
    height: 300px;
    background: #f8f8f8;

    img {
      width: 100%;
      height: 100%;
      object-fit: initial;
    }
  }

  .goods-info {
    padding: 16px;

    .goods-name {
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 12px;
    }

    .goods-tags {
      margin-bottom: 12px;

      .tag {
        display: inline-block;
        padding: 4px 8px;
        margin-right: 8px;
        background: #f7f8fa;
        color: #646566;
        border-radius: 16px;
        font-size: 12px;
      }
    }

    .goods-desc {
      font-size: 14px;
      color: #646566;
      line-height: 1.6;
    }
  }
}

.goods-container {
  padding: 0 12px;
}

.specs-section {
  padding: 0 16px 12px;

  .section-title {
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 8px;
  }
}

.options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .option-item {
    padding: 6px 12px;
    background-color: #f7f8fa;
    border-radius: 16px;
    font-size: 13px;
    cursor: pointer;
    border: 1px solid #f7f8fa;
    transition: all 0.2s;

    &.active {
      background-color: #fffbe8;
      border-color: #ffd4b1;
      color: #c58a53;
    }
  }
}

.bottom-bar {
  background-color: #fff;
  padding: 8px 16px;
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.05);

  .van-button {
    border-radius: 20px;
  }
}

.unit-list {
  display: flex;
  gap: 12px;
  margin: 12px 0;
}

.unit-item {
  padding: 6px 18px;
  border: 1px solid #ddd;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  background: #fff;
  color: #333;

  &.active {
    border-color: #1989fa;
    color: #1989fa;
    background: #e8f3ff;
  }
}
</style>